import { openDB } from "idb";
import axios from "axios";
import { readableOriginal } from "./photoUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ctn`;
const database = openDB("ctn-local-v1", 1, { upgrade(db) {
  for (const name of ["drafts", "photos", "cache"]) { const store = db.createObjectStore(name, { keyPath: "id" }); store.createIndex("scope", "scope"); }
} });
const emit = () => window.dispatchEvent(new Event("ctn-local-change"));
export const isLocalPhoto = (id) => id.startsWith("local:");
export async function readLocal(store, id, scope) { const row = await (await database).get(store, id); return row?.scope === scope ? row : null; }
export async function putLocal(store, value) { await (await database).put(store, value); emit(); return value; }
export async function listDrafts(scope) { return (await (await database).getAllFromIndex("drafts", "scope", scope)).sort((a,b) => b.updated.localeCompare(a.updated)); }
export async function cacheMetadata(scope, data) { await putLocal("cache", { id: scope, scope, data }); }
export async function cachedMetadata(scope) { return (await readLocal("cache", scope, scope))?.data; }
export async function saveDraft(scope, form, orderId) {
  const previous = await readLocal("drafts", form.client_id, scope);
  return putLocal("drafts", { id: form.client_id, scope, form, orderId: orderId || null, updated: new Date().toISOString(), status: "concept", error: "", created: previous?.created || new Date().toISOString() });
}
export async function deleteDraft(scope, id) {
  const draft = await readLocal("drafts", id, scope);
  if (!draft) return;
  const db = await database;
  const tx = db.transaction(["drafts", "photos"], "readwrite");
  for (const photoId of draft.form.foto_ids) if (isLocalPhoto(photoId)) tx.objectStore("photos").delete(photoId);
  tx.objectStore("drafts").delete(id); await tx.done; emit();
}
export async function storeOriginal(scope, file) {
  if (!file.size || file.size > 30 * 1024 * 1024) throw new Error("Een originele foto mag maximaal 30 MB zijn.");
  file = await readableOriginal(file, file.name);
  let preview = null;
  try {
    const image = await createImageBitmap(file), scale = Math.min(1, 640 / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas"); canvas.width = Math.round(image.width * scale); canvas.height = Math.round(image.height * scale);
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height); image.close();
    preview = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  } catch { /* HEIC can be previewed by the server after upload; original is never recompressed. */ }
  return putLocal("photos", { id: `local:${crypto.randomUUID()}`, scope, file, preview, name: file.name, size: file.size, serverId: null, aiRequest: null });
}
function sessionHeaders(scope) {
  const firmaId = scope.split(":")[1], token = localStorage.getItem("hr_token");
  if (!token || localStorage.getItem("hr_firma") !== firmaId) throw new Error("Kies eerst de juiste firma en meld je aan.");
  return { Authorization: `Bearer ${token}`, "X-Firma-Id": firmaId };
}
function assertSameSession(headers) {
  if (`Bearer ${localStorage.getItem("hr_token")}` !== headers.Authorization || localStorage.getItem("hr_firma") !== headers["X-Firma-Id"]) throw new Error("De sessie of firma is gewijzigd. Versturen is gestopt.");
}
const photoTransfers = new Map();
export async function uploadLocalPhoto(id, scope, progress) {
  if (!isLocalPhoto(id)) return id;
  if (photoTransfers.has(id)) return photoTransfers.get(id);
  const task = (async () => {
    let photo = await readLocal("photos", id, scope);
    if (!photo) throw new Error("Lokale foto ontbreekt op dit apparaat.");
    const headers = sessionHeaders(scope);
    if (!photo.serverId) {
      const memoryFile = await readableOriginal(photo.file, photo.name);
      const body = new FormData(); body.append("file", memoryFile, photo.name); body.append("client_id", id.slice(6));
      const { data } = await axios.post(`${API}/fotos`, body, { headers, timeout: 180000, onUploadProgress: (event) => progress?.(Math.round(event.loaded / Math.max(event.total || photo.size, 1) * 100)) });
      photo = { ...photo, serverId: data.id }; await putLocal("photos", photo);
    }
    assertSameSession(headers);
    if (photo.aiRequest) {
      const { data: job } = await axios.post(`${API}/fotos/${photo.serverId}/ai`, { aanvraag_id: photo.aiRequest }, { headers });
      photo = { ...photo, aiRequest: null, aiJobId: job.id }; await putLocal("photos", photo);
    }
    return photo.serverId;
  })();
  photoTransfers.set(id, task);
  try { return await task; } finally { photoTransfers.delete(id); }
}
export function orderPayload(form, purchase, sale) {
  const payload = Object.fromEntries(["productcode", "leverancier_id", "besteldatum", "stof", "lengte", "maten", "omschrijving", "foto_ids"].map((key) => [key, form[key]]));
  payload.categorie_id = form.categorie_id || null; payload.leverdatum = form.leverdatum || null;
  payload.verdeling = form.verdeling.map((r) => ({ kleur: r.kleur, bestemming_id: r.bestemming_id, aantal: Number(r.aantal) }));
  if (purchase) Object.assign(payload, { valuta: form.valuta, wisselkoers: Number(form.wisselkoers), koers_id: form.koers_id || null, aankoopprijs: form.aankoopprijs === "" || form.aankoopprijs == null ? null : Number(form.aankoopprijs) });
  if (sale) payload.verkoopprijs_eur = form.verkoopprijs_eur === "" || form.verkoopprijs_eur == null ? null : Number(form.verkoopprijs_eur);
  return payload;
}
const sending = new Map();
export async function sendDraft(scope, draftId, progress) {
  if (sending.has(draftId)) return sending.get(draftId);
  const task = (async () => {
    const draft = await readLocal("drafts", draftId, scope);
    if (!draft) throw new Error("Concept niet gevonden op dit apparaat.");
    const headers = sessionHeaders(scope);
    try {
      const { data: metadata } = await axios.get(`${API}/metadata`, { headers, timeout: 15000 });
      if (!metadata.rechten.ctn_beheren) throw new Error("Geen rechten om deze bestelling te versturen.");
      const { data: codeCheck } = await axios.get(`${API}/artikelcode-controle`, { headers, timeout: 15000, params: { productcode: draft.form.productcode, bestelling_id: draft.orderId || draft.form.client_id } });
      if (codeCheck.bestaat) {
        const error = new Error(codeCheck.melding);
        error.response = { status: 409, data: { detail: codeCheck.melding } };
        throw error;
      }
      const photos = [];
      for (let i = 0; i < draft.form.foto_ids.length; i++) {
        assertSameSession(headers);
        progress?.(`Foto ${i + 1}/${draft.form.foto_ids.length}`);
        photos.push(await uploadLocalPhoto(draft.form.foto_ids[i], scope, (pct) => progress?.(`Foto ${i + 1}/${draft.form.foto_ids.length} · ${pct}%`)));
      }
      assertSameSession(headers);
      const payload = orderPayload({ ...draft.form, foto_ids: photos }, metadata.rechten.ctn_aankoop, metadata.rechten.ctn_verkoop);
      if (draft.orderId) payload.versie = draft.form.versie; else payload.client_id = draft.form.client_id;
      const { data } = draft.orderId ? await axios.put(`${API}/bestellingen/${draft.orderId}`, payload, { headers }) : await axios.post(`${API}/bestellingen`, payload, { headers });
      await deleteDraft(scope, draftId); return data;
    } catch (error) {
      const message = typeof error.response?.data?.detail === "string" ? error.response.data.detail : error.response ? "Controleer de invoer voor je opnieuw verstuurt." : "Geen verbinding. Concept en originelen blijven op dit apparaat.";
      await putLocal("drafts", { ...draft, status: "wacht", error: message }); throw error;
    }
  })();
  sending.set(draftId, task);
  try { return await task; } finally { sending.delete(draftId); }
}